import React, { useEffect, useState } from 'react';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FORMATS = [
  { id: 'xlsx', label: 'Excel (.xlsx)', hint: 'All data: one workbook with a sheet for each list' },
  { id: 'csv', label: 'CSV', hint: 'All data: a .zip with one CSV per list' },
  { id: 'json', label: 'JSON', hint: 'For other software' },
];

const ExportData = ({ open, onClose }) => {
  const [datasets, setDatasets] = useState([]);
  const { companies: clients, selectedCompany } = useCompany();
  const [dataset, setDataset] = useState('all');
  const [format, setFormat] = useState('xlsx');
  const [clientId, setClientId] = useState(selectedCompany?.id || 'default');
  const [includeSsn, setIncludeSsn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const d = await fetch(`${API_URL}/api/export/datasets`, { credentials: 'include' });
      if (d.ok) setDatasets(await d.json());
    })();
  }, [open]);

  const touchesEmployees = dataset === 'all' || dataset === 'employees';

  const run = async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams({ format, company_id: clientId, include_ssn: String(includeSsn && touchesEmployees) });
      const res = await fetch(`${API_URL}/api/export/${dataset}?${qs}`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Export failed');
      }
      const ext = dataset === 'all' && format === 'csv' ? 'zip' : format;
      const name = `blueskies-${dataset}-${clientId}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.${ext}`;
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Exported ${name}`);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const pick = 'p-3 border rounded-lg text-sm text-left';
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle>Export data</DialogTitle>
          <DialogDescription>Download one client&apos;s data as a file you can open in Excel or other software.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label className="text-sm text-gray-600 flex flex-col gap-1">Client
            <select className="border rounded-md px-3 py-2 bg-white" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="export-client">
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>

          <div>
            <p className="text-sm text-gray-600 mb-2">What to export</p>
            <div className="grid grid-cols-2 gap-2 max-h-56 overflow-auto">
              <button className={`${pick} ${dataset === 'all' ? 'bg-emerald-50 border-emerald-500' : 'hover:bg-gray-50'}`} onClick={() => setDataset('all')} data-testid="export-all">All data</button>
              {datasets.map((d) => (
                <button key={d.id} className={`${pick} ${dataset === d.id ? 'bg-emerald-50 border-emerald-500' : 'hover:bg-gray-50'}`} onClick={() => setDataset(d.id)} data-testid={`export-${d.id}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-2">Format</p>
            <div className="flex gap-2">
              {FORMATS.map((f) => (
                <button key={f.id} title={f.hint} className={`px-3 py-2 border rounded-lg text-sm ${format === f.id ? 'bg-emerald-50 border-emerald-500' : 'hover:bg-gray-50'}`} onClick={() => setFormat(f.id)} data-testid={`format-${f.id}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {touchesEmployees && (
            <label className="flex items-start gap-2 text-sm text-gray-700 rounded border border-amber-200 bg-amber-50 p-3">
              <input type="checkbox" className="mt-1" checked={includeSsn} onChange={(e) => setIncludeSsn(e.target.checked)} data-testid="export-include-ssn" />
              <span>
                <AlertTriangle className="w-4 h-4 inline mr-1 text-amber-600" />
                Include full Social Security numbers. Off by default: SSNs are shown as ***-**-1234. Turn this on only if you need the real numbers, and keep the file safe.
              </span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={run} disabled={busy} data-testid="confirm-export">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportData;
